import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Tabs,
  Tab,
  Box,
  Avatar,
  Slide,
  Paper,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import AuthContext from "../store/AuthContext";
import useCommonContext from "../store/CommonContext";
import { api } from "../iconsImports";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SmallLoader from "../component/loading-screen/SmallLoader";
import { fileToBase64, splitIntoFlatChunks } from "../utils/ImageUtil";

const FieldCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "#f9f9f9",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Field = ({ label, value, type = "text" }) => (
  <Grid item xs={12} sm={6}>
    <FieldCard>
      <Typography variant="subtitle2" color="text.secondary" type={type}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }} type={type}>
        {value || "—"}
      </Typography>
    </FieldCard>
  </Grid>
);

const EditableField = ({ label, name, value, onChange, type = "text" }) => (
  <Grid item xs={12} sm={6}>
    <FieldCard>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <TextField
        fullWidth
        variant="standard"
        name={name}
        value={value || ""}
        onChange={onChange}
        type={type}
      />
    </FieldCard>
  </Grid>
);

// Tab Panel
const TabPanel = ({ children, value, index }) => {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
};
const EmployeeDetailsDialog = ({ open, onClose, employee, apiData }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState({
    aadhaar_img: null,
    pan_img: null,
    other_documents: null,
  });

  const authCtx = useContext(AuthContext);
  const user = authCtx && authCtx.user;
  const access = authCtx.token;
  const { refreshUser } = useCommonContext();

  // Initialize formData when employee changes
  useEffect(() => {
    if (employee) {
      // Merge employee profile data with apiData for complete form data
      const initialData = {
        ...employee.profile,
        name: apiData?.name || employee.profile?.name,
        email: apiData?.email || employee.profile?.email,
        username: apiData?.username || employee.profile?.username,
        gender: apiData?.gender || employee.profile?.gender,
        user_id: employee.id || employee.user_id,
      };
      setFormData(initialData);
    }
  }, [employee, apiData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const originalType =
        employee?.profile?.[name] !== undefined
          ? typeof employee.profile[name]
          : "string";

      if (originalType === "number") {
        const newValue = value === "" ? null : Number(value);
        return { ...prev, [name]: newValue };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleDocumentChange = (documentType, file) => {
    setDocuments((prev) => ({
      ...prev,
      [documentType]: file,
    }));
  };

  const handleUpdate = async () => {
    try {
      const payload = {};

      /* ---------------------------
         NORMAL FORM FIELDS
      ----------------------------*/
      Object.keys(formData).forEach((key) => {
        if (
          formData[key] !== null &&
          formData[key] !== undefined &&
          formData[key] !== "" &&
          key !== "user_id"
        ) {
          if (key === "aadhaar_number" || key === "pan_number") {
            payload[key] = String(formData[key]); // force string
          } else {
            payload[key] = formData[key];
          }
        }
      });
      
      payload.user_id = formData.user_id || employee?.id || employee?.user_id;

      if (!payload.user_id) {
        apiErrorToast("No user identifier found");
        return;
      }

      /* ---------------------------
         DOCUMENT IMAGE CHUNKS
      ----------------------------*/
      for (const [docKey, file] of Object.entries(documents)) {
        if (file) {
          const base64 = await fileToBase64(file);
          payload[docKey] = splitIntoFlatChunks(base64, docKey);
        }
      }

      postJsonData(
        ApiEndpoints.UPDATE_EMPLOYEE,
        payload,
        setLoading,
        (res) => {
          okSuccessToast(res.data.message);
          refreshUser();
          setIsEditMode(false);
          setDocuments({
            aadhaar_img: null,
            pan_img: null,
            other_documents: null,
          });
          onClose();
        },
        (err) => apiErrorToast(err)
      );
    } catch (err) {
      console.error(err);
      apiErrorToast("Update failed");
    }
  };

  const handleTabChange = (_, newValue) => setTabIndex(newValue);

  // Document upload component with image preview
  const DocumentUploadField = ({
    label,
    documentType,
    accept = "image/*,.pdf,.doc,.docx",
  }) => (
    <Grid item xs={12} sm={6}>
      <FieldCard>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Image Preview */}
          {(documents[documentType] || employee?.profile?.[documentType]) && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {documents[documentType] ? (
                <img
                  src={URL.createObjectURL(documents[documentType])}
                  alt={label}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "150px",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                />
              ) : employee?.profile?.[documentType] ? (
                <img
                  src={employee.profile[documentType]}
                  alt={label}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "150px",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                />
              ) : null}
            </Box>
          )}

          {/* Upload Button */}
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<CloudUploadIcon />}
            size="small"
          >
            Upload {label}
            <input
              type="file"
              accept={accept}
              hidden
              onChange={(e) =>
                handleDocumentChange(documentType, e.target.files[0])
              }
            />
          </Button>

          {/* File Info */}
          {documents[documentType] && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2" noWrap>
                {documents[documentType].name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({Math.round(documents[documentType].size / 1024)} KB)
              </Typography>
            </Box>
          )}
        </Box>
      </FieldCard>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: "#ffffff",
        },
      }}
    >
      <SmallLoader loading={loading} />
      <DialogTitle
        sx={{
          bgcolor: "#e0f7fa",
          color: "#0277bd",
          py: 2,
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "1.3rem",
        }}
      >
        Employee Profile
      </DialogTitle>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        textColor="primary"
        indicatorColor="primary"
        sx={{
          bgcolor: "#e0f7fa",
          px: 3,
        }}
      >
        <Tab label="Personal" />
        <Tab label="Contact" />
        <Tab label="Bank" />
        <Tab label="Employment" />
        <Tab label="Salary" />
        <Tab label="ID Proofs" />
      </Tabs>

      <DialogContent dividers sx={{ px: 3, py: 2, bgcolor: "#fafcfe" }}>
        {/* Existing TabPanels for Personal, Contact, Bank, Employment, Salary */}
        <TabPanel value={tabIndex} index={0}>
          <Grid container spacing={2}>
            {isEditMode ? (
              <>
                <EditableField
                  label="Full Name"
                  name="name"
                  value={formData.name || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Gender"
                  name="gender"
                  value={formData.gender || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={formData.dob || ""}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <Field label="Full Name" value={formData.name} />
                <Field label="Gender" value={formData.gender} />
                <Field label="Date of Birth" value={formData.dob} />
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          <Grid container spacing={2}>
            {isEditMode ? (
              <>
                <EditableField
                  label="Email"
                  name="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Mobile Number"
                  name="username"
                  value={formData.username || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Residential Address"
                  name="address"
                  value={formData.address || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Emergency Contact"
                  name="emergency_number"
                  value={formData.emergency_number || ""}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <Field label="Email" value={formData.email} />
                <Field label="Mobile Number" value={formData.username} />
                <Field label="Residential Address" value={formData.address} />
                <Field
                  label="Emergency Contact"
                  value={formData.emergency_number}
                />
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={2}>
          <Grid container spacing={2}>
            {isEditMode ? (
              <>
                <EditableField
                  label="Bank Name"
                  name="bank_name"
                  value={formData.bank_name || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Account Number"
                  name="account_no"
                  value={formData.account_no || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="IFSC Code"
                  name="ifsc"
                  value={formData.ifsc || ""}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <Field label="Bank Name" value={formData.bank_name} />
                <Field label="Account Number" value={formData.account_no} />
                <Field label="IFSC Code" value={formData.ifsc} />
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={3}>
          <Grid container spacing={2}>
            {isEditMode ? (
              <>
                <EditableField
                  label="Designation"
                  name="designation"
                  value={formData.designation || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Date of Joining"
                  name="date_of_joining"
                  type="date"
                  value={formData.date_of_joining || ""}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <Field label="Designation" value={formData.designation} />
                <Field
                  label="Date of Joining"
                  value={formData.date_of_joining}
                />
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={4}>
          <Grid container spacing={2}>
            {isEditMode ? (
              <>
                <EditableField
                  label="Initial Payscale"
                  name="payscale_at_joining"
                  value={formData.payscale_at_joining || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Last Increment Date"
                  name="last_increment_date"
                  type="date"
                  value={formData.last_increment_date || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Last Increment (%)"
                  name="last_increment_percentage"
                  value={formData.last_increment_percentage || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Increment Amount"
                  name="last_increment_amount"
                  value={formData.last_increment_amount || ""}
                  onChange={handleChange}
                />
                <EditableField
                  label="Current CTC"
                  name="current_ctc"
                  value={formData.current_ctc || ""}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <Field
                  label="Initial Payscale"
                  value={formData.payscale_at_joining}
                />
                <Field
                  label="Last Increment Date"
                  value={formData.last_increment_date}
                />
                <Field
                  label="Last Increment (%)"
                  value={formData.last_increment_percentage}
                />
                <Field
                  label="Increment Amount"
                  value={formData.last_increment_amount}
                />
                <Field label="Current CTC" value={formData.current_ctc} />
              </>
            )}
          </Grid>
        </TabPanel>

        {/* ID Proofs Tab - Now with text fields for numbers */}
        <TabPanel value={tabIndex} index={5}>
          <Grid container spacing={2}>
            {isEditMode ? (
              <>
                <EditableField
                  label="Aadhaar Number"
                  name="aadhaar_number"
                  value={formData.aadhaar_number || ""}
                  onChange={handleChange}
                  type="text"
                />
                <EditableField
                  label="PAN Number"
                  name="pan_number"
                  value={formData.pan_number || ""}
                  onChange={handleChange}
                  type="text"
                />
                {/* Additional ID fields can be added here */}
                <Grid item xs={12}>
                  <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                    Document Images
                  </Typography>
                </Grid>
                <DocumentUploadField
                  label="Aadhaar Card Front"
                  documentType="aadhaar_img"
                  accept="image/*"
                />
                <DocumentUploadField
                  label="Aadhaar Card Back"
                  documentType="aadhaar_back"
                  accept="image/*"
                />
                <DocumentUploadField
                  label="PAN Card"
                  documentType="pan_img"
                  accept="image/*"
                />
              </>
            ) : (
              <>
                <Field label="Aadhaar Number" value={formData.aadhaar_number} />
                <Field label="PAN Number" value={formData.pan_number} />
                {/* Document preview in view mode */}
                {(employee?.profile?.aadhaar_img ||
                  employee?.profile?.aadhaar_back ||
                  employee?.profile?.pan_img) && (
                  <Grid item xs={12}>
                    <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                      Document Images
                    </Typography>
                    <Grid container spacing={2}>
                      {employee?.profile?.aadhaar_img && (
                        <Grid item xs={12} sm={4}>
                          <FieldCard>
                            <Typography variant="subtitle2" gutterBottom>
                              Aadhaar Front
                            </Typography>
                            <img
                              src={employee?.profile?.aadhaar_img}
                              alt="Aadhaar Front"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "120px",
                                borderRadius: "4px",
                              }}
                            />
                          </FieldCard>
                        </Grid>
                      )}
                      {employee?.profile?.aadhaar_back && (
                        <Grid item xs={12} sm={4}>
                          <FieldCard>
                            <Typography variant="subtitle2" gutterBottom>
                              Aadhaar Back
                            </Typography>
                            <img
                              src={employee.profile.aadhaar_back}
                              alt="Aadhaar Back"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "120px",
                                borderRadius: "4px",
                              }}
                            />
                          </FieldCard>
                        </Grid>
                      )}
                      {employee?.profile?.pan_img && (
                        <Grid item xs={12} sm={4}>
                          <FieldCard>
                            <Typography variant="subtitle2" gutterBottom>
                              PAN Card
                            </Typography>
                            <img
                              src={employee.profile.pan_img}
                              alt="PAN Card"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "120px",
                                borderRadius: "4px",
                              }}
                            />
                          </FieldCard>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, justifyContent: "center", bgcolor: "#f4fafd" }}
      >
        {isEditMode ? (
          <>
            <Button
              variant="outlined"
              onClick={() => setIsEditMode(false)}
              sx={{ px: 4, borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={loading}
              sx={{
                backgroundColor: "#4caf50",
                color: "#fff",
                px: 4,
                borderRadius: 2,
                ml: 2,
              }}
            >
              Save
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={() => setIsEditMode(true)}
              sx={{ px: 4, borderRadius: 2 }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                backgroundColor: "#03a9f4",
                color: "#fff",
                px: 4,
                fontWeight: 500,
                borderRadius: 2,
                ml: 2,
              }}
            >
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
export default EmployeeDetailsDialog;
